'use client';

import * as React from 'react';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useOverlay } from '@/components/hooks/useOverlay';
import { OverlayKey } from '@/lib/enums/overlayKey.enum';
import Overlay from '@/components/ui/Overlay';
import LayoutOverlay from '@/components/ui/LayoutOverlay';
import Block from '@/components/ui/Block';
import OverlayTitle from '@/components/ui/LayoutOverlay/OverlayTitle';
import { AuthContext, User } from '@/components/auth/AuthProvider';
import Button from '@/components/ui/Button';
import Buttons from '@/components/ui/Buttons';
import { downloadFile } from '@/lib/company/downloadFile';
import { IMarketingListToolFilter } from '@/components/content/MarketingListTool/interfaces/marketingListToolFilter.interface';
import { getPayload, initialData, MarketingListExportType } from '@/components/content/MarketingListTool/DownloadResults';
import { useTRPC } from '@/trpc/client';
import { downloadUrlQueryOptions, limitsQueryOptions, marketingKeys } from '@/lib/queries/marketingQueries';
import QueryErrorState from '@/components/fallback/QueryErrorState';

interface IProps {
  user: User | null;
  filter: IMarketingListToolFilter;
  type: MarketingListExportType;
}

const OverlayMarketingListExtraDownload: React.FC<IProps> = ({ user, type, filter }) => {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { orderCode, orderInitialized } = React.useContext(AuthContext);
  const { isOverlayOpen, zIndexOfOverlay, closeOverlay } = useOverlay(OverlayKey.marketing_list_extra_download);
  const [loading, setLoading] = useState(false);

  const downloadUrlQuery = useQuery(downloadUrlQueryOptions({ trpc, type }));
  const limitsQuery = useQuery(limitsQueryOptions({ trpc, enabled: orderInitialized }));
  const downloadUrl = downloadUrlQuery.data ?? '#';
  const limits = limitsQuery.data ?? initialData;

  const onClose = () => {
    closeOverlay();
  };

  return (
    <Overlay
      isOpen={isOverlayOpen}
      onClose={onClose}
      layout="default"
      zIndex={zIndexOfOverlay}
      hideClose={true}
    >
      <LayoutOverlay size="default">
        <Block
          width="narrow"
          theme="lighter"
        >
          <OverlayTitle onClose={onClose} />
          {(downloadUrlQuery.isError || limitsQuery.isError) && <QueryErrorState error={downloadUrlQuery.error ?? limitsQuery.error} />}
          {!downloadUrlQuery.isError && !limitsQuery.isError && (
            <React.Fragment>
              <p>
                <b>
                  {t('report.extraDownload.description', {
                    maxAllowed: limits.maxAllowedExports,
                    price: limits.exportPrice,
                  })}
                </b>
              </p>
              <Buttons>
                <Button
                  title={t('report.extraDownload.confirm')}
                  theme="primary"
                  size="large"
                  loading={loading}
                  disabled={loading || downloadUrlQuery.isPending || limitsQuery.isPending}
                  onClick={() => {
                    setLoading(true);
                    void downloadFile(downloadUrl, user, orderCode, 'POST', JSON.stringify(getPayload(type, filter))).finally(() => {
                      setLoading(false);
                      void queryClient.invalidateQueries({ queryKey: marketingKeys.limits(trpc) });
                      onClose();
                    });
                  }}
                />
                <Button
                  title={t('report.extraDownload.close')}
                  theme="secondary"
                  size="large"
                  onClick={onClose}
                />
              </Buttons>
            </React.Fragment>
          )}
        </Block>
      </LayoutOverlay>
    </Overlay>
  );
};

export default OverlayMarketingListExtraDownload;
